import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TelXLPanelComponent } from '@dxp/elements';
import { IContactSelected, IWorkItem } from '@dxp/core/models';
import { VoiceService, SettingsService } from '@dxp/core/services';
import { recalculateHeight } from '@dxp/core/helpers';
import { CONTACT_STORE, WORKITEMS_STORE } from '@dxp/core/stores';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { concatMap, filter, pairwise, startWith, tap, timer } from 'rxjs';
import { CustomerInteractionsComponent } from '@dxp/customer-interactions';
import { ContactDirectoriesComponent } from '@dxp/contact-directories';
import { VoiceContactComponent } from '../contacts/contact.component';
import { VoiceCallComponent } from './components/voice-call/voice-call.component';
import { VoiceCallNewComponent } from './components/voice-call-new/voice-call-new.component';

@Component({
    selector: 'voice-container',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        RouterModule,
        VoiceCallComponent,
        TelXLPanelComponent,
        VoiceCallNewComponent,
        VoiceContactComponent,
        CustomerInteractionsComponent,
        ContactDirectoriesComponent,
    ],
    templateUrl: './voice-container.component.html',
    styleUrls: ['./voice-container.component.scss'],
})
export class VoiceContainerComponent implements OnInit {
    readonly #workItemStore = inject(WORKITEMS_STORE);
    readonly #route = inject(ActivatedRoute);
    readonly #voiceService = inject(VoiceService);
    readonly #settingsService = inject(SettingsService);
    protected contactStore = inject(CONTACT_STORE);

    workItemId = signal<string | null>(null);
    showContacts = signal(false);
    newCallContact = signal<IContactSelected | null>(null);

    /**
     * Temporarily adds a class to the element to perform an exit animation
     */
    showTemporaryExitStyling = signal(false);

    /**
     * Only shows the edit panel if the following tabs are selected
     * Tabs:
     *  contacts  -> External Directory
     *  customers -> End Customers
     */
    showCustomerEditPanel = computed(() =>
        ['contacts', 'customers'].includes(this.contactStore.currentTab()),
    );

    contacts = computed(() => this.#voiceService.contacts());
    conversationId = computed(
        () => this.workItem()?.primaryConversationId ?? undefined,
    );
    workItem = computed<IWorkItem | null>(() => {
        const workItemId = this.workItemId;
        if (!workItemId) return null;
        return (
            this.#workItemStore
                .workItems()
                .find(item => item.workItemId === workItemId()) || null
        );
    });
    height = computed<string>(() =>
        recalculateHeight('communication', this.#settingsService.viewType()),
    );

    activatedRoute$ = this.#route.paramMap.pipe(takeUntilDestroyed());

    /**
     * Listen to when the tab changes
     * So we can clear the previous selected contact
     */
    currentTabChange$ = toObservable(this.contactStore.currentTab).pipe(
        takeUntilDestroyed(),
        tap(() => {
            /**
             * Reset the contact when the tab is changed
             */
            this.contactStore.clearSelectedContact();
        }),
    );

    /**
     * Listening to the changes in the show edit customer panel signal to add a temporary class
     */
    showEditCustomerPanel$ = toObservable(this.showCustomerEditPanel).pipe(
        takeUntilDestroyed(),
        /**
         * As we're using pair wise - we will always start with a falsey value
         * This is because the agents is the first tab & it will always be closed on the agents tab
         */
        startWith(false),
        /**
         * Pairwise is going to get the current and previous value
         */
        pairwise(),
        /**
         * We'll filter the emissions, so we only check the change from showing the panel to not showing the panel
         */
        filter(([previous, current]) => previous && !current),
        /**
         * On the first emission we start to remove the panel
         */
        tap(() => this.showTemporaryExitStyling.set(true)),
        /**
         * Once the tap has fired - we've start the timer
         * DO NOT CHANGE [ The timer value - 400 ] - Linked to CSS animation - Prevents Jarring
         */
        concatMap(() =>
            timer(400).pipe(
                tap(() => {
                    this.showTemporaryExitStyling.set(false);
                }),
            ),
        ),
    );

    ngOnInit() {
        this.activatedRoute$.subscribe(params => {
            const id = params.get('workItemId');
            this.workItemId.set(id);
            if (id && id !== 'new') {
                this.#voiceService.setWorkItem(id);
            }
        });

        /**
         * Reset the contact when the component is initialized
         */
        this.contactStore.clearSelectedContact();

        /**
         * Subscribe to the changes - That adds and removes the temp class for the exit animation
         */
        this.showEditCustomerPanel$.subscribe();

        /**
         * Subscribe to the changes - Reset the contact selection on tab change
         */
        this.currentTabChange$.subscribe();
    }

    contactsClose(close: boolean) {
        if (close) {
            this.#voiceService.setContacts(false, '', '', undefined, false);
            this.showContacts.set(false);
        }
    }

    contactSelected(contact: IContactSelected, newCall: boolean) {
        this.#voiceService.contactSelected(contact);
        if (newCall) {
            this.newCallContact.set(contact);
        }
    }
}
